import { useState, useEffect } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail, uploadImage } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ImagePlus, Trash2, Loader2 } from "lucide-react";

export default function Gallery() {
  const { t, lang } = useLang();
  const [biz, setBiz] = useState(null);
  const [caption, setCaption] = useState("");
  const [uploading, setUploading] = useState(false);

  const load = () => api.get("/businesses/mine").then((r) => setBiz(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadImage(file);
      await api.post("/businesses/portfolio", { url, caption });
      setCaption("");
      toast.success(lang === "es" ? "Imagen agregada" : "Image added");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const remove = async (id) => {
    await api.delete(`/businesses/portfolio/${id}`);
    toast.success(lang === "es" ? "Imagen eliminada" : "Image removed");
    load();
  };

  const items = biz?.portfolio || [];

  return (
    <div>
      <h1 className="font-head text-2xl font-extrabold tracking-tight text-secondary">{t("dash_gallery")}</h1>
      <p className="mt-1 text-sm text-slate-500">{lang === "es" ? "Muestra tus mejores trabajos. Aparecerán en tu perfil público." : "Show your best work. It appears on your public profile."}</p>

      <div className="mt-6 flex flex-col gap-3 rounded-sm border border-slate-200 bg-white p-4 sm:flex-row sm:items-center">
        <Input value={caption} onChange={(e) => setCaption(e.target.value)} placeholder={lang === "es" ? "Descripción (opcional)" : "Caption (optional)"} className="rounded-sm sm:max-w-xs" data-testid="gallery-caption-input" />
        <label className={`inline-flex cursor-pointer items-center gap-2 rounded-sm bg-primary px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-orange-600 ${uploading ? "opacity-60" : ""}`} data-testid="gallery-upload-label">
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
          {lang === "es" ? "Subir imagen" : "Upload image"}
          <input type="file" accept="image/*" onChange={onUpload} disabled={uploading} className="hidden" data-testid="gallery-upload-input" />
        </label>
      </div>

      <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4" data-testid="gallery-grid">
        {items.map((it) => (
          <div key={it.id} className="group relative overflow-hidden rounded-sm border border-slate-200 bg-white" data-testid={`gallery-item-${it.id}`}>
            <img src={it.url} alt={it.caption || "trabajo"} className="h-40 w-full object-cover" />
            {it.caption && <p className="truncate p-2 text-xs text-slate-600">{it.caption}</p>}
            <button onClick={() => remove(it.id)} className="absolute right-2 top-2 rounded-sm bg-white/90 p-1.5 text-slate-500 opacity-0 transition-opacity hover:text-destructive group-hover:opacity-100" data-testid={`gallery-delete-${it.id}`}>
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
        {items.length === 0 && <p className="col-span-full py-12 text-center text-slate-400">—</p>}
      </div>
    </div>
  );
}
